import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useReviewKycCase } from "@/features/kyc/api/use-review-kyc-case";
import type { ReviewKycCaseRequestDto } from "@/features/kyc/api/dto";
import {
  createKycCaseReviewSchema,
  type KycCaseReviewFormValues,
} from "@/features/kyc/schemas/kyc-case.schema";

/**
 * Décision finale du back-office sur un dossier KYC
 * (`POST /KycCases/{id}/review`) — APPROVED ou REJECTED. Le
 * `reviewedByKeycloakId` est le claim `sub` de l'opérateur connecté
 * (session Keycloak) ; un rejet DOIT être motivé (notes obligatoire).
 */
export function KycCaseReviewDialog({
  open,
  onOpenChange,
  kycCaseId,
  decision,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kycCaseId: string;
  /** Décision portée par le bouton qui ouvre le dialog (Approuver/Rejeter). */
  decision: ReviewKycCaseRequestDto["decision"];
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createKycCaseReviewSchema(), [i18n.language]);

  const reviewKycCase = useReviewKycCase(kycCaseId);
  const pending = reviewKycCase.isPending;
  const [preview, setPreview] = useState<ReviewKycCaseRequestDto | null>(null);

  const form = useForm({
    defaultValues: { decision, notes: "" } as KycCaseReviewFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse({ ...value, decision });
      if (!parsed.success) return;
      const reviewedByKeycloakId = useSessionStore.getState().userInfo?.sub;
      if (typeof reviewedByKeycloakId !== "string" || !reviewedByKeycloakId) {
        notifSilenceError(t("error"));
        return;
      }
      const payload: ReviewKycCaseRequestDto = {
        decision,
        reviewedByKeycloakId,
        notes: parsed.data.notes?.trim() || undefined,
      };
      setPreview(payload);
    },
  });

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              decision === "APPROVED"
                ? "kyc.cases.review.approveTitle"
                : "kyc.cases.review.rejectTitle"
            )}
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="notes"
            validators={{
              onChange: ({ value }) =>
                decision === "REJECTED" && !value?.trim()
                  ? t("requiredFieldError")
                  : undefined,
            }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="notes">{t("kyc.cases.form.notes")}</Label>
                <Textarea
                  id="notes"
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant={decision === "REJECTED" ? "destructive" : "default"}
              disabled={pending}
            >
              {pending ? <ButtonSpinner /> : null}
              {t(decision === "APPROVED" ? "kyc.cases.approve" : "kyc.cases.reject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t(
        decision === "APPROVED"
          ? "kyc.cases.review.approveTitle"
          : "kyc.cases.review.rejectTitle"
      )}
      description={t("common.mutationPreview.description")}
      confirmLabel={t(
        decision === "APPROVED" ? "kyc.cases.approve" : "kyc.cases.reject"
      )}
      t={(key) => t(key as never)}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        reviewKycCase.mutate(preview, {
          onSuccess: () => {
            notifSilence(t("kyc.cases.feedback.reviewed"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
